import React from 'react'
import ReactMarkdown from 'react-markdown'
import { IMAGE_DOMAIN, STATUS_KEYWORDS } from '../config'
import type { SkillKey } from '../types'

interface MarkdownRendererProps {
  content: string
  skill: SkillKey
  isStreaming?: boolean
}

/**
 * 加载状态组件
 */
const StatusLoader: React.FC<{ text: string }> = ({ text }) => (
  <span className="status-loader">
    <span className="status-spinner"></span>
    <span className="status-text">{text}</span>
  </span>
)

/**
 * 提取状态信息
 * 返回：{ cleanContent: 移除状态后的内容, activeStatus: 当前活动的状态文字（如果有） }
 */
function extractStatusInfo(content: string, isStreaming: boolean): {
  cleanContent: string
  activeStatus: string | null
} {
  if (!content) return { cleanContent: content, activeStatus: null }

  // 找到所有状态文字的位置
  const statusPositions: Array<{ index: number; keyword: string }> = []

  for (const keyword of STATUS_KEYWORDS) {
    let searchPos = 0
    while (true) {
      const idx = content.indexOf(keyword, searchPos)
      if (idx === -1) break
      statusPositions.push({ index: idx, keyword })
      searchPos = idx + keyword.length
    }
  }

  if (statusPositions.length === 0) {
    return { cleanContent: content, activeStatus: null }
  }

  // 按位置排序，找到最后一个
  statusPositions.sort((a, b) => a.index - b.index)
  const lastStatus = statusPositions[statusPositions.length - 1]

  // 检查最后一个状态后面是否有非状态内容
  const afterLastStatus = content.slice(lastStatus.index + lastStatus.keyword.length)
  const hasRealContent = afterLastStatus.trim().length > 0

  // 构建清理后的内容：移除所有状态文字
  let cleanContent = content
  for (const pos of statusPositions) {
    cleanContent = cleanContent.replace(pos.keyword, '')
  }

  // 流式输出且最后一个状态后面没有实际内容，显示加载动画
  const activeStatus = (isStreaming && !hasRealContent) ? lastStatus.keyword : null

  return { cleanContent, activeStatus }
}

/**
 * 预处理裸 URL，将纯 URL 转换为 Markdown 链接格式
 */
function preprocessBareUrls(content: string): string {
  if (!content) return content

  const protectedItems: string[] = []

  // 保护 Markdown 图片
  let result = content.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, (match) => {
    protectedItems.push(match)
    return `\x00PROT${protectedItems.length - 1}\x00`
  })

  // 保护 Markdown 链接
  result = result.replace(/\[([^\]]*)\]\(([^)]+)\)/g, (match) => {
    protectedItems.push(match)
    return `\x00PROT${protectedItems.length - 1}\x00`
  })

  // 保护 HTML 链接
  result = result.replace(/<a\s+[^>]*>[^<]*<\/a>/gi, (match) => {
    protectedItems.push(match)
    return `\x00PROT${protectedItems.length - 1}\x00`
  })

  // 保护其他 HTML 标签
  result = result.replace(/<\/?[a-zA-Z][^>]*>/g, (match) => {
    protectedItems.push(match)
    return `\x00PROT${protectedItems.length - 1}\x00`
  })

  // 转换裸 URL
  const urlPattern = /(https?:\/\/[a-zA-Z0-9.\-_/?:=&%#~+]+)/g
  result = result.replace(urlPattern, (url) => `[${url}](${url})`)

  // 恢复保护的内容
  protectedItems.forEach((item, index) => {
    result = result.replace(`\x00PROT${index}\x00`, item)
  })

  return result
}

/**
 * React Markdown 渲染器
 */
export const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({
  content,
  skill,
  isStreaming = false
}) => {
  // 仅 learning 技能处理状态文字
  const shouldExtractStatus = skill === 'learning'

  // 提取状态信息，获取清理后的内容
  const { cleanContent, activeStatus } = shouldExtractStatus
    ? extractStatusInfo(content, isStreaming)
    : { cleanContent: content, activeStatus: null }

  // 预处理 URL
  let processed = preprocessBareUrls(cleanContent)

  // 处理图片链接 - 添加域名前缀
  if (skill === 'activity' || skill === 'system') {
    processed = processed.replace(
      /!\[([^\]]*)\]\((\/files\/[^)]+)\)/g,
      (_match, alt, path) => `![${alt}](${IMAGE_DOMAIN}${path})`
    )
  }

  return (
    <>
      {/* 加载状态动画 */}
      {activeStatus && <StatusLoader text={activeStatus} />}

      {/* Markdown 内容 */}
      <ReactMarkdown
        remarkPlugins={[]}
        components={{
          a: ({ href, children, ...props }) => {
            const linkText = skill === 'learning' ? '作品链接' : children
            return (
              <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
                {linkText}
              </a>
            )
          },
          img: ({ src, alt, ...props }) => (
            <img src={src} alt={alt} loading="lazy" {...props} />
          ),
          p: ({ children }) => <p>{children}</p>
        }}
      >
        {processed}
      </ReactMarkdown>
    </>
  )
}